import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import './styles.css';
import { initAuth } from './api/auth';
import { AppProvider, useApp } from './state/AppContext';
import { Shell } from './components/Shell';
import { DailySummary } from './screens/DailySummary';
import { Incidents } from './screens/Incidents';
import { DataImport } from './screens/DataImport';
import { Dashboard } from './screens/Dashboard';
import { ScorecardSummary } from './screens/ScorecardSummary';
import { EmployeeDetailScreen } from './screens/EmployeeDetail';
import { Employees } from './screens/Employees';
import { PunchData } from './screens/PunchData';
import { Schedules } from './screens/Schedules';
import { Settings } from './screens/Settings';
import { Administration } from './screens/Administration';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Approvals change scores immediately, so a stale window would show a
      // score that disagrees with the feed beneath it.
      staleTime: 10_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/** Sends each role to the first screen it can actually see. */
function Landing() {
  const { can, me } = useApp();
  const first =
    (can('dailySummary', 'view') && '/daily') ||
    (can('dashboard', 'view') && '/dashboard') ||
    (can('scorecard', 'view') && '/scorecard') ||
    (can('dataImport', 'view') && '/import') ||
    (can('incidents', 'view') && '/incidents') ||
    (can('scorecard', 'viewSelf') && me.employeeId != null && `/employee/${me.employeeId}`) ||
    '/scorecard';
  return <Navigate to={first} replace />;
}

function render() {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppProvider>
            <Routes>
              <Route element={<Shell />}>
                <Route index element={<Landing />} />
                <Route path="/daily" element={<DailySummary />} />
                <Route path="/incidents" element={<Incidents />} />
                <Route path="/import" element={<DataImport />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/scorecard" element={<ScorecardSummary />} />
                <Route path="/employee/:employeeId" element={<EmployeeDetailScreen />} />
                <Route path="/employees" element={<Employees />} />
                <Route path="/punches" element={<PunchData />} />
                <Route path="/schedules" element={<Schedules />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/admin" element={<Administration />} />
                <Route path="*" element={<Landing />} />
              </Route>
            </Routes>
          </AppProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}

// Nothing renders until we know how to authenticate. With Entra that means a
// token is in hand; without it, the browser has already left for Microsoft and
// this page is being replaced.
initAuth().then(render).catch((err: Error) => {
  document.getElementById('root')!.innerHTML =
    `<div style="font:14px system-ui;padding:40px;max-width:640px">`
    + `<h2 style="margin:0 0 8px">The scorecard could not start</h2>`
    + `<p style="color:#5a5852">${err.message}</p>`
    + `<p style="color:#9a9890;font-size:12px">Check that the API is running and reachable.</p>`
    + `</div>`;
});
